import { useState, useEffect } from 'react';
import CookieConsent from '../../components/feature/CookieConsent';

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4thit1t85rh3e48p6s0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        const successMessage = document.createElement('div');
        successMessage.className = 'fixed top-4 right-4 bg-lime-400 text-slate-800 px-6 py-4 rounded-lg shadow-lg z-50 flex items-center space-x-3';
        successMessage.innerHTML = '<i class="ri-check-line text-xl"></i><span class="font-medium">Thank you for your inquiry. We will respond within 24 hours.</span>';
        document.body.appendChild(successMessage);
        
        setTimeout(() => {
          successMessage.remove();
        }, 5000);
        
        form.reset();
      } else {
        const errorMessage = document.createElement('div');
        errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex items-center space-x-3';
        errorMessage.innerHTML = '<i class="ri-error-warning-line text-xl"></i><span class="font-medium">There was an error submitting your form. Please try again.</span>';
        document.body.appendChild(errorMessage);
        
        setTimeout(() => {
          errorMessage.remove();
        }, 5000);
      }
    } catch (error) {
      const errorMessage = document.createElement('div');
      errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex items-center space-x-3';
      errorMessage.innerHTML = '<i class="ri-error-warning-line text-xl"></i><span class="font-medium">There was an error submitting your form. Please try again.</span>';
      document.body.appendChild(errorMessage);
      
      setTimeout(() => {
        errorMessage.remove();
      }, 5000);
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return (
    <>
      <title>Veridian Vanguard Advisors</title>
      <meta name="description" content="Veridian Vanguard Advisors provides bespoke multi-layered solutions for governments, ultra-high-net-worth individuals, elite athletes, institutions, and business leaders requiring unparalleled advisory expertise." />
      <meta name="keywords" content="Veridian Vanguard Advisors" />
      <link rel="canonical" href={siteUrl} />
      <meta name="last-modified" content={new Date().toISOString()} />
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Veridian Vanguard Advisors" />
      <meta property="og:description" content="Veridian Vanguard Advisors provides bespoke multi-layered solutions for governments, ultra-high-net-worth individuals, elite athletes, institutions, and business leaders requiring unparalleled advisory expertise." />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:site_name" content="Veridian Vanguard Advisors" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Veridian Vanguard Advisors" />
      <meta name="twitter:description" content="Veridian Vanguard Advisors provides bespoke multi-layered solutions for governments, ultra-high-net-worth individuals, elite athletes, institutions, and business leaders requiring unparalleled advisory expertise." />
      
      {/* Geo Tags */}
      <meta name="geo.region" content="US-CA" />
      <meta name="geo.placename" content="Los Angeles" />
      <meta name="geo.position" content="34.0522;-118.2437" />
      <meta name="ICBM" content="34.0522, -118.2437" />
      
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Veridian Vanguard Advisors",
          "description": "Veridian Vanguard Advisors provides bespoke multi-layered solutions for governments, ultra-high-net-worth individuals, elite athletes, institutions, and business leaders requiring unparalleled advisory expertise.",
          "url": siteUrl,
          "address": [
            {
              "@type": "PostalAddress",
              "addressLocality": "New York",
              "addressCountry": "US"
            },
            {
              "@type": "PostalAddress",
              "addressLocality": "London",
              "addressCountry": "GB"
            },
            {
              "@type": "PostalAddress",
              "addressLocality": "Los Angeles",
              "addressRegion": "CA",
              "addressCountry": "US"
            }
          ],
          "email": "connect@veridianvanguard.com",
          "areaServed": ["US", "GB"],
          "serviceType": ["Strategic Advisory", "Elite Consulting", "High-Net-Worth Advisory", "Government Consulting", "Institutional Advisory"]
        })}
      </script>
      
      <div className="bg-slate-800 text-white min-h-screen font-sans">
        {/* Navigation */}
        <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-slate-800/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
        }`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-2xl font-light tracking-wide">VERIDIAN VANGUARD</span>
              </div>
              <div className="hidden md:flex space-x-8">
                <button 
                  onClick={() => scrollToSection('home')}
                  className="text-white hover:text-lime-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Home
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-white hover:text-lime-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Contact
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
            <div className="max-w-4xl space-y-8">
              <div className="inline-flex items-center space-x-3 bg-slate-800/50 px-4 py-2 rounded-full border border-lime-400/30">
                <div className="w-2 h-2 bg-lime-400 rounded-full"></div>
                <span className="text-sm uppercase tracking-wider text-white/90">Advisory Excellence Since 2025</span>
              </div>
              
              <h1 className="text-6xl lg:text-7xl xl:text-8xl font-extralight tracking-wider leading-tight">
                VERIDIAN<br />
                VANGUARD<br />
                <span className="text-lime-400">ADVISORS</span>
              </h1>
              
              <div className="space-y-6 max-w-2xl">
                <p className="text-2xl text-white/90 font-medium">
                  We operate at the nexus of strategy, influence, and discretion
                </p>
                <p className="text-lg text-white/70 leading-relaxed">
                  Providing bespoke multi-layered solutions that suffice the unique needs of governments, 
                  ultra-high-net-worth individuals, elite athletes, institutions, family offices, 
                  philanthropists, business leaders, and cultural icons who require unparalleled advisory expertise.
                </p>
                <p className="text-lg text-white/70 leading-relaxed">
                  We are the bridge between vision and execution helping our clients move with clarity, confidence, 
                  and precision in environments where reputation, timing, and trust matter most.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-lime-400 text-slate-800 px-8 py-4 rounded-lg font-bold uppercase tracking-wide hover:bg-lime-300 transition-all transform hover:scale-105 flex items-center justify-center cursor-pointer whitespace-nowrap"
                >
                  Define Your Next Move
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-5 gap-12">
              <div className="lg:col-span-2 space-y-8">
                <h2 className="text-5xl font-extralight leading-tight">
                  LET&apos;S DISCUSS<br />
                  <span className="text-lime-400">YOUR VISION</span>
                </h2>
                
                <p className="text-lg text-white/70 leading-relaxed">
                  Our advisory team is ready to craft bespoke solutions for your unique challenges. 
                  Connect with us to explore how we can elevate your strategic position.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <i className="ri-mail-line text-lime-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                    <a 
                      href="mailto:connect@veridianvanguard.com" 
                      className="text-white hover:text-lime-400 transition-colors cursor-pointer"
                    >
                      connect@veridianvanguard.com
                    </a>
                  </div>
                </div>
                
                <div className="bg-lime-400 text-slate-800 px-6 py-3 rounded-full inline-block font-medium">
                  <i className="ri-time-line mr-2"></i>
                  RESPONSE WITHIN 24 HOURS
                </div>
              </div>
              
              <div className="lg:col-span-3">
                <form onSubmit={handleSubmit} className="bg-white/3 backdrop-blur-sm p-8 rounded-xl space-y-6" data-readdy-form>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <input
                        type="text"
                        name="fullName"
                        placeholder="Full Name"
                        required
                        className="w-full bg-transparent border-0 border-b border-lime-400 text-white placeholder-white/40 py-4 focus:outline-none focus:border-lime-300 transition-colors text-base"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        required
                        className="w-full bg-transparent border-0 border-b border-lime-400 text-white placeholder-white/40 py-4 focus:outline-none focus:border-lime-300 transition-colors text-base"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <input
                      type="text"
                      name="organization"
                      placeholder="Organization/Affiliation"
                      className="w-full bg-transparent border-0 border-b border-lime-400 text-white placeholder-white/40 py-4 focus:outline-none focus:border-lime-300 transition-colors text-base"
                    />
                  </div>
                  
                  <div>
                    <textarea
                      name="message"
                      placeholder="Tell us about your strategic objectives..."
                      rows={5}
                      maxLength={500}
                      className="w-full bg-transparent border-0 border-b border-lime-400 text-white placeholder-white/40 py-4 focus:outline-none focus:border-lime-300 transition-colors resize-none text-base"
                    ></textarea>
                    <div className="text-right text-xs text-white/40 mt-1">Max 500 characters</div>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-lime-400 text-slate-800 py-4 rounded-lg font-bold uppercase tracking-wide hover:bg-lime-300 transition-colors flex items-center justify-center cursor-pointer whitespace-nowrap"
                  >
                    Submit Inquiry
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                  
                  <p className="text-xs text-white/50 text-center">
                    Your information is confidential and secure
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black py-20 rounded-t-lg">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-12">
              <h3 className="text-lime-400 text-sm uppercase tracking-wider font-medium mb-6">LOCATIONS</h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="space-y-2">
                  <div className="text-white font-medium text-lg">New York</div>
                  <div className="text-white/60 text-sm">United States</div>
                </div>
                <div className="space-y-2">
                  <div className="text-white font-medium text-lg">London</div>
                  <div className="text-white/60 text-sm">United Kingdom</div>
                </div>
                <div className="space-y-2">
                  <div className="text-white font-medium text-lg">Los Angeles</div>
                  <div className="text-white/60 text-sm">United States</div>
                </div>
              </div>
            </div>
            
            <div className="border-t border-lime-400/20 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-xs text-gray-500 text-center">
                © 2025 Veridian Vanguard Advisors. All Rights Reserved.
              </div>
            </div>
          </div>
        </footer>

        {/* Cookie Consent Banner */}
        <CookieConsent />
      </div>
    </>
  );
};

export default HomePage;
